import React, { useState } from 'react';
import cn from 'clsx';

import Collapsible from '../../../../library/collapsible/collapsible';
import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import CirclesRating from '../../../../components/circles-rating/circles-rating';
import CarBumpRegularIcon from '../../../../shared/icons/car-bump-regular-icon';
import RoadRegularIcon from '../../../../shared/icons/road-regular-icon';
import SteeringWheelRegularIcon from '../../../../shared/icons/steering-wheel-regular-icon';
import TireLightIcon from '../../../../shared/icons/tire-light-icon';
import TreadwellTireRegularIcon from '../../../../shared/icons/treadwell-tire-regular-icon';
import VolumeRegularIcon from '../../../../shared/icons/volume-regular-icon';

import { useMessage } from '../../../../shared/hooks/hooks';

import { ProductData } from '../../../../shared/types/graphql-types';
import { TREADWELL_NO_RECOMMENDATION_DATA_MESSAGE } from '../../../../shared/constants/messages';

import styles from './product-list-review-details.scss';

interface ProductListReviewDetailsProps {
  isTreadwellTabSelected?: boolean;
  numberOfReviews?: number;
  onClickReadReviewsFn?: () => void;
  product?: ProductData;
  productDetailUrl?: string;
}

function ProductListReviewDetails(props: ProductListReviewDetailsProps) {
  const {
    isTreadwellTabSelected,
    numberOfReviews = 0,
    onClickReadReviewsFn,
    product,
    productDetailUrl = ''
  } = props;
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);

  const messages = useMessage([TREADWELL_NO_RECOMMENDATION_DATA_MESSAGE]);
  const { corneringSteering, rideComfort, rideNoise, treadLife } =
    product || {};

  const shouldShowReviewDetails =
    Boolean(corneringSteering) ||
    Boolean(rideComfort) ||
    Boolean(rideNoise) ||
    (Boolean(treadLife) && numberOfReviews > 0);

  return (
    <>
      {process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' &&
        shouldShowReviewDetails && (
          <div
            className={cn(
              styles['collapsible-container'],
              'product-list-review-details__collapsible-container'
            )}
          >
            <Collapsible
              label={
                <div className={styles['collapsible-header']}>
                  <div>CUSTOMER REVIEWS</div>
                  <div className={styles['collapsible-view-details']}>
                    {isReviewsOpen ? 'hide ' : 'view '}details
                  </div>
                </div>
              }
              onToggle={() => setIsReviewsOpen(!isReviewsOpen)}
              toggleType="CHEVRON"
            >
              <>
                <div className={styles['alt']}>
                  <div className={styles['header']} />
                  <div className={styles['footer']}>
                    {Boolean(rideComfort) && (
                      <Typography
                        className={styles['sub-description']}
                        tag="div"
                      >
                        <div className={styles['label-group']}>
                          <CarBumpRegularIcon className={styles['icon']} />
                          <span className={styles['label']}>Ride Comfort</span>
                        </div>
                        <CirclesRating rating={rideComfort || 0} />
                      </Typography>
                    )}
                    {Boolean(corneringSteering) && (
                      <Typography
                        className={styles['sub-description']}
                        tag="div"
                      >
                        <div className={styles['label-group']}>
                          <SteeringWheelRegularIcon
                            className={styles['icon']}
                          />
                          <span className={styles['label']}>
                            Cornering/Steering
                          </span>
                        </div>
                        <CirclesRating rating={corneringSteering || 0} />
                      </Typography>
                    )}
                    {Boolean(rideNoise) && (
                      <Typography
                        className={styles['sub-description']}
                        tag="div"
                      >
                        <div className={styles['label-group']}>
                          <VolumeRegularIcon className={styles['icon']} />
                          <span className={styles['label']}>Ride Noise</span>
                        </div>
                        <CirclesRating rating={rideNoise || 0} />
                      </Typography>
                    )}
                    {Boolean(treadLife) && (
                      <Typography
                        className={styles['sub-description']}
                        tag="div"
                      >
                        <div className={styles['label-group']}>
                          <RoadRegularIcon className={styles['icon']} />
                          <span className={styles['label']}>Tread Life</span>
                        </div>
                        <CirclesRating rating={treadLife || 0} />
                      </Typography>
                    )}
                  </div>
                </div>
                <Link
                  className={styles['read-reviews']}
                  onClick={onClickReadReviewsFn}
                  size="small"
                  to={`${productDetailUrl}#read-reviews`}
                >
                  see all reviews
                </Link>
              </>
            </Collapsible>
          </div>
        )}

      {process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true' && (
        <div
          className={
            shouldShowReviewDetails ? styles['alt'] : styles['container']
          }
        >
          {shouldShowReviewDetails && (
            <div className={styles['header']}>
              <Typography className={styles['title']} tag="p">
                Customer Reviews
              </Typography>
            </div>
          )}

          {shouldShowReviewDetails ? (
            <>
              <div className={styles['alt']}>
                <div className={styles['header']} />
                <div className={styles['footer']}>
                  {Boolean(rideComfort) && (
                    <Typography className={styles['sub-description']} tag="div">
                      <div className={styles['label-group']}>
                        <CarBumpRegularIcon className={styles['icon']} />
                        <span className={styles['label']}>Ride Comfort</span>
                      </div>
                      <CirclesRating rating={rideComfort || 0} />
                    </Typography>
                  )}
                  {Boolean(corneringSteering) && (
                    <Typography className={styles['sub-description']} tag="div">
                      <div className={styles['label-group']}>
                        <SteeringWheelRegularIcon className={styles['icon']} />
                        <span className={styles['label']}>
                          Cornering/Steering
                        </span>
                      </div>
                      <CirclesRating rating={corneringSteering || 0} />
                    </Typography>
                  )}
                  {Boolean(rideNoise) && (
                    <Typography className={styles['sub-description']} tag="div">
                      <div className={styles['label-group']}>
                        <VolumeRegularIcon className={styles['icon']} />
                        <span className={styles['label']}>Ride Noise</span>
                      </div>
                      <CirclesRating rating={rideNoise || 0} />
                    </Typography>
                  )}
                  {Boolean(treadLife) && (
                    <Typography className={styles['sub-description']} tag="div">
                      <div className={styles['label-group']}>
                        <RoadRegularIcon className={styles['icon']} />
                        <span className={styles['label']}>Tread Life</span>
                      </div>
                      <CirclesRating rating={treadLife || 0} />
                    </Typography>
                  )}
                </div>
              </div>
              <Link
                className={styles['read-reviews']}
                onClick={onClickReadReviewsFn}
                size="small"
                to={`${productDetailUrl}#read-reviews`}
              >
                see all reviews
              </Link>
            </>
          ) : (
            <Typography
              className={styles['no-data']}
              size="extraSmall"
              tag="span"
            >
              {isTreadwellTabSelected ? (
                <>
                  <TreadwellTireRegularIcon
                    className={styles['treadwell-icon']}
                  />
                  {messages?.[TREADWELL_NO_RECOMMENDATION_DATA_MESSAGE] ||
                    'This tire is not currently Treadwell tested.'}
                </>
              ) : (
                <>
                  <TireLightIcon />
                  There are currently no reviews for this product.
                </>
              )}
            </Typography>
          )}
        </div>
      )}
    </>
  );
}

export default ProductListReviewDetails;
